import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, Avatar, Paper, List, ListItem, ListItemText, ListItemIcon } from '@mui/material';
import { Business as BusinessIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/i18n/types/i18nNamespace';
import type { I18nScenarioEditorNs, I18nPersonasNs } from '@/i18n/dictionaries/interfaces';
import { PersonaDto } from '@/api-gen/api.schemas';
import { getAvatarUrlWithSas } from '@/app/utils/avatarUtils';
import { PersonaSharingChip } from '@/features/personas';

interface SharePersonaDialogContentProps {
  persona: PersonaDto;
  missingCompanyNames: string[];
}

export const SharePersonaDialogContent: React.FC<SharePersonaDialogContentProps> = ({
  persona,
  missingCompanyNames,
}) => {
  const [translate] = useTranslation([I18nNamespace.ScenarioEditor]);
  const [translatePersonas] = useTranslation([I18nNamespace.Personas]);
  const [avatarLoadError, setAvatarLoadError] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const getInitials = (name?: string) => {
    if (!name) return 'P';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getAvatarColor = (id?: number) => {
    const colors = [
      '#1976d2', '#388e3c', '#d32f2f', '#7b1fa2',
      '#c2185b', '#0288d1', '#0097a7', '#00796b'
    ];
    return colors[(id || 0) % colors.length];
  };

  const personaName = persona.name || translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.unnamedPersona));

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', bgcolor: 'background.default', borderColor: 'divider' }}>
        <Avatar
          sx={{
            bgcolor: getAvatarColor(persona.id),
            width: 56,
            height: 56,
            mr: 2
          }}
          src={!avatarLoadError && persona.avatarUrl ? getAvatarUrlWithSas(persona.avatarUrl) : undefined}
          alt={personaName}
          imgProps={{ onError: () => setAvatarLoadError(true) }}
        >
          {getInitials(persona.name)}
        </Avatar>
        <Box sx={{ overflow: 'hidden', flex: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6" component="div" noWrap>
              {personaName}
            </Typography>
            <PersonaSharingChip
              persona={persona}
              tooltipText={translatePersonas(nameof.full<I18nPersonasNs>((n) => n.card.sharedWithTooltip))}
              size="small"
            />
          </Box>
          {persona.description && (
             <Typography variant="body2" color="text.secondary" noWrap>
              {persona.description}
            </Typography>
          )}
        </Box>
      </Paper>

      <Typography variant="body1" sx={{ mb: 2 }}>
        {translate(
          nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.sharePersonaDescription),
          { personaName }
        )}
      </Typography>

      <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto', bgcolor: 'background.paper' }}>
        <List dense disablePadding>
          {missingCompanyNames.map((companyName, index) => (
            <ListItem key={index} divider={index < missingCompanyNames.length - 1}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <BusinessIcon fontSize="small" color="action" />
              </ListItemIcon>
              <ListItemText 
                primary={companyName} 
                primaryTypographyProps={{ variant: 'body2' }} 
              />
            </ListItem>
          ))}
        </List>
      </Paper>
    </Box>
  );
};